import { useState } from "react";

import { Minus, Plus } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Textarea } from "~/components/ui/textarea";
import { useCreateStockAdjustment } from "~/hooks/purchasing";
import { useToast } from "~/hooks/use-toast";
import type { IngredientType, OilType, RawMaterial } from "~/types/purchasing";
import { MATERIAL_UNITS, OIL_UNITS } from "~/types/purchasing";
import { litersToKg } from "~/utils/oilConversions";

interface StockAdjustmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ingredientType: IngredientType;
  ingredient: OilType | RawMaterial;
}

export function StockAdjustmentDialog({
  open,
  onOpenChange,
  ingredientType,
  ingredient,
}: StockAdjustmentDialogProps) {
  const { toast } = useToast();
  const createAdjustment = useCreateStockAdjustment();

  const [adjustmentType, setAdjustmentType] = useState<"add" | "subtract">(
    "add"
  );
  const [quantity, setQuantity] = useState<string>("");
  const [unit, setUnit] = useState<string>(
    ingredientType === "oil" ? "liters" : "units"
  );
  const [notes, setNotes] = useState<string>("");

  const unitOptions = ingredientType === "oil" ? OIL_UNITS : MATERIAL_UNITS;

  const ingredientName =
    ingredientType === "oil"
      ? (ingredient as OilType).name
      : (ingredient as RawMaterial).name;

  const currentStock =
    ingredientType === "oil"
      ? (ingredient as OilType).current_stock_liters
      : (ingredient as RawMaterial).current_stock;

  const formatStock = () => {
    if (ingredientType === "oil") {
      // Oils are stored in liters but bought/counted in kg → display in kg.
      const density = (ingredient as OilType).density_kg_per_liter;
      if (density && density > 0) {
        return `${litersToKg(currentStock, density).toLocaleString("es-CL", {
          maximumFractionDigits: 2,
        })} kg`;
      }
      return `${currentStock.toFixed(2)} Lt`;
    }
    return `${currentStock} ${(ingredient as RawMaterial).unit}`;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const qty = parseFloat(quantity);
    if (isNaN(qty) || qty <= 0) {
      toast({
        title: "Error",
        description: "Ingrese una cantidad válida mayor a 0",
        variant: "destructive",
      });
      return;
    }

    if (!notes.trim()) {
      toast({
        title: "Error",
        description: "Debe ingresar una nota explicando el ajuste",
        variant: "destructive",
      });
      return;
    }

    const finalQuantity = adjustmentType === "subtract" ? -qty : qty;

    createAdjustment.mutate(
      {
        ingredient_type: ingredientType,
        oil_type_id: ingredientType === "oil" ? ingredient.id : undefined,
        raw_material_id:
          ingredientType === "raw_material" ? ingredient.id : undefined,
        quantity: finalQuantity,
        unit,
        notes: notes.trim(),
      },
      {
        onSuccess: () => {
          toast({
            title: "Ajuste realizado",
            description: `Stock de ${ingredientName} ajustado correctamente`,
          });
          handleClose();
        },
        onError: (error) => {
          toast({
            title: "Error",
            description: error.message,
            variant: "destructive",
          });
        },
      }
    );
  };

  const handleClose = () => {
    setAdjustmentType("add");
    setQuantity("");
    setUnit(ingredientType === "oil" ? "liters" : "units");
    setNotes("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Ajuste de Stock</DialogTitle>
          <DialogDescription>
            Ajustar stock de{" "}
            <span className="font-medium text-foreground">
              {ingredientName}
            </span>
            <br />
            <span className="text-xs">Stock actual: {formatStock()}</span>
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            {/* Adjustment Type */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label className="text-right">Tipo *</Label>
              <div className="col-span-3 flex gap-2">
                <Button
                  type="button"
                  variant={adjustmentType === "add" ? "default" : "outline"}
                  className={`flex-1 ${
                    adjustmentType === "add"
                      ? "bg-green-600 hover:bg-green-700"
                      : ""
                  }`}
                  onClick={() => setAdjustmentType("add")}
                >
                  <Plus className="mr-1 h-4 w-4" />
                  Agregar
                </Button>
                <Button
                  type="button"
                  variant={
                    adjustmentType === "subtract" ? "default" : "outline"
                  }
                  className={`flex-1 ${
                    adjustmentType === "subtract"
                      ? "bg-red-600 hover:bg-red-700"
                      : ""
                  }`}
                  onClick={() => setAdjustmentType("subtract")}
                >
                  <Minus className="mr-1 h-4 w-4" />
                  Descontar
                </Button>
              </div>
            </div>

            {/* Quantity */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="quantity" className="text-right">
                Cantidad *
              </Label>
              <div className="col-span-3 flex gap-2">
                <Input
                  id="quantity"
                  type="number"
                  step="0.01"
                  min="0"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder="Ej: 100"
                  className="flex-1"
                />
                <Select value={unit} onValueChange={setUnit}>
                  <SelectTrigger className="w-[120px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {unitOptions.map((u) => (
                      <SelectItem key={u.value} value={u.value}>
                        {u.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Notes */}
            <div className="grid grid-cols-4 items-start gap-4">
              <Label htmlFor="notes" className="pt-2 text-right">
                Nota *
              </Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Explique el motivo del ajuste (ej: Corrección de inventario, Merma, etc.)"
                className="col-span-3"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={createAdjustment.isPending}
              className={
                adjustmentType === "add"
                  ? "bg-green-600 hover:bg-green-700"
                  : "bg-red-600 hover:bg-red-700"
              }
            >
              {createAdjustment.isPending
                ? "Guardando..."
                : adjustmentType === "add"
                  ? "Agregar Stock"
                  : "Descontar Stock"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
